import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, isRef as _isRef, vModelSelect as _vModelSelect, normalizeProps as _normalizeProps, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createVNode as _createVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["id", "name", "disabled", "required", "aria-describedby"]
const _hoisted_2 = {
  value: "",
  disabled: ""
}
const _hoisted_3 = ["value"]
const _hoisted_4 = ["for"]
const _hoisted_5 = ["data-key", "onClick"]
const _hoisted_6 = ["src"]

import { ref, Ref, computed, nextTick, watch, toRef } from "vue";
import SvgIcon from "./SvgIcon/SvgIcon.vue";
import HintText from "./SharedForm/HintText.vue";
import ValidationText from "./SharedForm/ValidationText.vue";
import { useBreakpoints, onClickOutside, onKeyStroke } from "@vueuse/core";
import useInputValidation from "../composables/use-input-validation";
import type { HTMLInputElementEvent, InputEventDetails } from "../types/SharedInput.type";


const __default__ = {
	model: {
		prop: "modelValue",
		event: "update:modelValue",
	},
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'SharedDropdown',
  props: {
    disabled: { type: Boolean },
    displayName: {},
    hint: {},
    hintPermanent: { type: Boolean },
    icons: {},
    imageUrls: {},
    label: {},
    modelValue: {},
    name: {},
    options: { default: () => ({}) },
    placeholder: {},
    required: { type: Boolean },
    value: {},
    forcedErrorMessage: {},
    requiredErrorMessage: {},
    dataTestName: { default: "" },
    ariaDescribedby: {}
  },
  emits: ["update:modelValue", "change-error-msg"],
  setup(__props: any, { emit }) {

const props = __props;





const nativeSelectRef = ref<HTMLSelectElement | null>(null);
const dropDownWrapperRef = ref<HTMLDivElement | null>(null);
const customOptionsListRef = ref<HTMLUListElement | null>(null);

const isEm64 = useBreakpoints({ em64: "64em" }).greaterOrEqual("em64");
const isCustomDropdown = computed(() => isEm64.value);
const customOptionsList_isOpen = ref(false);
const customOptionsList_focusedOptionKey = ref<string | null | undefined>(null);

const { isValidationVisible, errorMessage, handleInput } = useInputValidation(
	nativeSelectRef,
	props.displayName || props.name || "Feld",
	undefined,
	toRef(props, "forcedErrorMessage"),
	props.requiredErrorMessage
);

watch([isValidationVisible, errorMessage], () => {
	emit("change-error-msg", event as HTMLInputElementEvent, { errorMessage: errorMessage.value });
});

const selectedOption =
	props.modelValue === undefined
		? ref(props.value || null)
		: computed({
				get() {
					return props.modelValue;
				},
				set(val) {
					emit("update:modelValue", val || null);
				},
		  });

const hasError = computed(() => isValidationVisible.value);
const isPopulated = computed(() => !!selectedOption.value);

// Methods
const handleMousedownOnSelect = (e: Event) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const customDropDown_focusSelected = (customOptionsList_focusedOptionKey?: string | null | undefined) => {
	(
		customOptionsListRef.value?.querySelector(`button[data-key=${customOptionsList_focusedOptionKey}]`) as HTMLElement
	)?.focus();
};

const customDropDown_close = () => {
	customOptionsList_isOpen.value = false;
};

const customDropDown_toggle = () => {
	customOptionsList_isOpen.value = !customOptionsList_isOpen.value;
	if (!isCustomDropdown.value) return;

	if (customOptionsList_isOpen.value && selectedOption.value)
		nextTick(() => {
			customOptionsList_focusedOptionKey.value = selectedOption.value;
			customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
		});
};

const focusNativeSelect = () => {
	nextTick(() => nativeSelectRef.value?.focus());
};

const customDropDown_select = (key: string | number) => {
	selectedOption.value = String(key);
	customDropDown_close();
	focusNativeSelect();
};

const handleSpaceAndEnter = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value || customOptionsList_isOpen.value) return;
	e.preventDefault();
	customDropDown_toggle();
};

const handleArrowUpAndDown = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	e.preventDefault();
	if (!customOptionsList_isOpen.value) return customDropDown_toggle();

	const optionsKeys = Object.keys(props.options);
	const focusedIndex = optionsKeys.indexOf(customOptionsList_focusedOptionKey.value || "");
	const add = e.key === "ArrowUp" ? -1 : 1;
	const newIndex = Math.min(Math.max(0, focusedIndex + add), optionsKeys.length - 1);
	customOptionsList_focusedOptionKey.value = optionsKeys[newIndex];

	customDropDown_focusSelected(customOptionsList_focusedOptionKey.value);
};

const handleTap = (e: KeyboardEvent) => {
	if (!isCustomDropdown.value) return;
	if (customOptionsList_isOpen.value) {
		e.preventDefault();
		const key = document.activeElement?.getAttribute("data-key");
		if (key) customDropDown_select(key);
		customDropDown_close();
		focusNativeSelect();
	}
};

const handleEscape = () => {
	customDropDown_close();
	focusNativeSelect();
};

onKeyStroke([" ", "Spacebar", "Enter"], (e) => handleSpaceAndEnter(e), {
	target: nativeSelectRef as Ref<HTMLSelectElement>,
});

onKeyStroke(["ArrowUp", "ArrowDown"], (e: KeyboardEvent) => handleArrowUpAndDown(e), {
	target: dropDownWrapperRef as Ref<HTMLDivElement>,
});

onKeyStroke("Tab", (e: KeyboardEvent) => handleTap(e), { target: dropDownWrapperRef as Ref<HTMLDivElement> });

onKeyStroke("Escape", () => handleEscape(), { target: dropDownWrapperRef as Ref<HTMLDivElement> });

onClickOutside(dropDownWrapperRef, () => customDropDown_close());

watch(
	() => selectedOption.value,
	() => {
		customDropDown_close();
		handleInput();
	}
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", {
      ref_key: "dropDownWrapperRef",
      ref: dropDownWrapperRef,
      class: _normalizeClass(["tw group relative text-base", {
				// Font für Label
				'text-gray-80': !customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value,
				'focus-within:text-black-primary hover:text-black-primary ': !_ctx.disabled && !hasError.value,
				'text-black-primary': customOptionsList_isOpen.value,
				'text-gray-50': _ctx.disabled,
				'text-error': hasError.value,
			}])
    }, [
      _withDirectives(_createElementVNode("select", _normalizeProps({
        id: _ctx.name,
        ref_key: "nativeSelectRef",
        ref: nativeSelectRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedOption) ? (selectedOption).value = $event : null)),
        name: _ctx.name,
        class: ["relative flex h-15 w-full select-none appearance-none items-center rounded-t-lg pl-4 pr-10 pt-[0.8125em] text-black-primary shadow-borderBottom1 outline-none focus-visible:shadow-inset2", {
					// color // bestimmt auch die Farbe der Linie / Umrandung
					'text-gray-80': !customOptionsList_isOpen.value && !_ctx.disabled && !hasError.value && !isPopulated.value,
					'text-black-primary': customOptionsList_isOpen.value && !hasError.value,
					'text-error': hasError.value,
					'hover:text-black-primary focus-visible:text-black-primary ': !_ctx.disabled && !hasError.value,
					// border active
					'shadow-borderBottom2': customOptionsList_isOpen.value,
					// background
					'bg-gray-40': customOptionsList_isOpen.value,
					'bg-gray-20': !customOptionsList_isOpen.value,
					'hover:bg-gray-40 focus:bg-gray-40': !_ctx.disabled,
					'hover:bg-gray-40': !hasError.value && !_ctx.disabled,
					// disabled / enabled
					'cursor-pointer': !_ctx.disabled,
					'pointer-events-auto cursor-not-allowed text-gray-50': _ctx.disabled,
				}],
        style: {"-webkit-tap-highlight-color":"transparent"},
        disabled: _ctx.disabled,
        required: _ctx.required,
        [_ctx.dataTestName || ""]: '',
        "aria-describedby": _ctx.ariaDescribedby,
        onMousedown: handleMousedownOnSelect
      }), [
        _createElementVNode("option", _hoisted_2, _toDisplayString(_ctx.placeholder), 1),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (optionValue, key) => {
          return (_openBlock(), _createElementBlock("option", {
            key: optionValue,
            value: key
          }, _toDisplayString(optionValue), 9, _hoisted_3))
        }), 128))
      ], 16, _hoisted_1), [
        [_vModelSelect, _unref(selectedOption)]
      ]),
      _createElementVNode("label", {
        for: _ctx.name,
        class: _normalizeClass(["pointer-events-none absolute left-4 right-12 top-0 z-10 flex h-15 origin-left select-none items-center overflow-hidden whitespace-nowrap transition-[opacity,_transform]", {
					'-translate-y-[0.9285714rem] scale-75': customOptionsList_isOpen.value || _unref(selectedOption),
				}])
      }, _toDisplayString(_ctx.placeholder), 11, _hoisted_4),
      _createVNode(SvgIcon, {
        class: _normalizeClass(["pointer-events-none absolute right-4 top-1/2 size-5 shrink-0 -translate-y-1/2 transition-transform", { 'rotate-180': customOptionsList_isOpen.value, 'text-black-primary': isPopulated.value && !_ctx.disabled }]),
        "icon-name": "arrow-down"
      }, null, 8, ["class"]),
      _createElementVNode("ul", {
        ref_key: "customOptionsListRef",
        ref: customOptionsListRef,
        class: _normalizeClass(["absolute left-0 top-15 z-20 w-full text-black-primary shadow-4", { hidden: !customOptionsList_isOpen.value || !isCustomDropdown.value }]),
        style: {"clip-path":"inset(0 -20px -20px -20px)"}
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option, key, i) => {
          return (_openBlock(), _createElementBlock("li", { key: key }, [
            _createElementVNode("button", {
              class: "flex h-12 w-full select-none items-center bg-white px-4 outline-none hover:bg-gray-40 hover:text-black focus:bg-gray-40 focus:text-black",
              style: {"-webkit-tap-highlight-color":"transparent"},
              "data-key": key,
              type: "button",
              tabindex: "-1",
              onClick: ($event: any) => (customDropDown_select(key))
            }, [
              (_ctx.icons && _ctx.icons[i])
                ? (_openBlock(), _createBlock(SvgIcon, {
                    key: 0,
                    class: "mr-2 size-5 shrink-0",
                    "icon-name": _ctx.icons[i]
                  }, null, 8, ["icon-name"]))
                : (_ctx.imageUrls && _ctx.imageUrls[i])
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 1,
                      src: _ctx.imageUrls[i],
                      alt: "",
                      class: "mr-2 size-5 shrink-0"
                    }, null, 8, _hoisted_6))
                  : _createCommentVNode("", true),
              _createElementVNode("span", {
                class: _normalizeClass(["block flex-1 text-left", { 'ml-7': _ctx.icons && !_ctx.icons[i] }])
              }, _toDisplayString(option), 3)
            ], 8, _hoisted_5)
          ]))
        }), 128))
      ], 2)
    ], 2),
    (_ctx.hint && (_ctx.hintPermanent || !_unref(isValidationVisible)))
      ? (_openBlock(), _createBlock(HintText, {
          key: 0,
          "hint-text": _ctx.hint
        }, null, 8, ["hint-text"]))
      : _createCommentVNode("", true),
    (_unref(isValidationVisible))
      ? (_openBlock(), _createBlock(ValidationText, {
          key: 1,
          "data-test-name": _ctx.dataTestName,
          "error-message": _unref(errorMessage)
        }, null, 8, ["data-test-name", "error-message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})